import { NavLink } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import {
  LayoutDashboard,
  List,
  QrCode,
  Users,
  Package,
  Receipt,
  BarChart3,
  CreditCard,
  Building2,
  User,
  Car,
  ShoppingCart,
  FileText,
  ClipboardList,
  Percent,
  Shield,
  ChevronDown,
  ChevronRight,
  Sparkles,
  Zap,
  TrendingUp,
  Settings,
  Gift,
} from 'lucide-react'
import { cn } from '../utils/cn'
import { useState, useEffect, useMemo } from 'react'
import { roleService } from '../services/roleService'

// Grouped navigation structure
const navigationGroups = {
  customer: [
    {
      name: 'Main',
      icon: Sparkles,
      color: 'from-blue-500 to-purple-600',
      items: [
        { name: 'Dashboard', href: '/customer', icon: LayoutDashboard },
        { name: 'My QR Code', href: '/customer/qr', icon: QrCode },
        { name: 'Wash History', href: '/customer/history', icon: Car },
        { name: 'Profile', href: '/customer/profile', icon: User },
      ]
    }
  ],
  staff: [
    {
      name: 'Operations',
      icon: Zap,
      color: 'from-green-500 to-emerald-600',
      items: [
        { name: 'Dashboard', href: '/', icon: LayoutDashboard },
        { name: 'Wash Queue', href: '/wash-queue', icon: List },
        { name: 'Scan QR', href: '/scan', icon: QrCode },
        { name: 'POS', href: '/pos', icon: CreditCard },
        { name: 'Cash-ups', href: '/cash-ups', icon: FileText, permission: 'perform_cash_up' },
      ]
    },
    {
      name: 'Business',
      icon: TrendingUp,
      color: 'from-blue-500 to-indigo-600',
      items: [
        { name: 'Customers', href: '/customers', icon: Users, permission: 'view_customers' },
        { name: 'Products', href: '/products', icon: Package, permission: 'manage_products' },
        { name: 'Inventory', href: '/inventory', icon: Package, permission: 'manage_inventory' },
        { name: 'Expenses', href: '/expenses', icon: Receipt, permission: 'manage_expenses' },
        { name: 'Subscriptions', href: '/subscriptions', icon: CreditCard, permission: 'manage_subscriptions' },
        { name: 'Wash Catalog', href: '/wash-catalog', icon: ClipboardList, permission: 'manage_wash_catalog' },
        { name: 'Dealers', href: '/dealers', icon: Building2, permission: 'manage_dealers' },
      ]
    },
    {
      name: 'Management',
      icon: Users,
      color: 'from-orange-500 to-red-500',
      items: [
        { name: 'Customers', href: '/customers', icon: Users },
        { name: 'Profile', href: '/profile', icon: User },
      ]
    }
  ],
  manager: [
    {
      name: 'Operations',
      icon: Zap,
      color: 'from-green-500 to-emerald-600',
      items: [
        { name: 'Dashboard', href: '/', icon: LayoutDashboard },
        { name: 'Wash Queue', href: '/wash-queue', icon: List },
        { name: 'Scan QR', href: '/scan', icon: QrCode },
        { name: 'POS', href: '/pos', icon: ShoppingCart },
      ]
    },
    {
      name: 'Business',
      icon: TrendingUp,
      color: 'from-blue-500 to-indigo-600',
      items: [
        { name: 'Customers', href: '/customers', icon: Users, permission: 'view_customers' },
        { name: 'Products', href: '/products', icon: Package, permission: 'manage_products' },
        { name: 'Inventory', href: '/inventory', icon: Package, permission: 'manage_inventory' },
        { name: 'Expenses', href: '/expenses', icon: Receipt, permission: 'manage_expenses' },
        { name: 'Subscriptions', href: '/subscriptions', icon: CreditCard, permission: 'manage_subscriptions' },
        { name: 'Wash Catalog', href: '/wash-catalog', icon: ClipboardList, permission: 'manage_wash_catalog' },
        { name: 'Cash-ups', href: '/cash-ups', icon: FileText, permission: 'perform_cash_up' },
      ]
    },
    {
      name: 'Analytics',
      icon: BarChart3,
      color: 'from-purple-500 to-pink-600',
      items: [
        { name: 'Reports', href: '/reports', icon: BarChart3, permission: 'view_reports' },
        { name: 'Dealers', href: '/dealers', icon: Building2, permission: 'manage_dealers' },
      ]
    },
    {
      name: 'Administration',
      icon: Settings,
      color: 'from-gray-600 to-gray-800',
      items: [
        { name: 'User Management', href: '/user-management', icon: Shield },
        { name: 'Profile', href: '/profile', icon: User },
      ]
    }
  ],
  owner: [
    {
      name: 'Operations',
      icon: Zap,
      color: 'from-green-500 to-emerald-600',
      items: [
        { name: 'Dashboard', href: '/', icon: LayoutDashboard },
        { name: 'Wash Queue', href: '/wash-queue', icon: List },
        { name: 'Wash History', href: '/wash-history', icon: Car },
        { name: 'Scan QR', href: '/scan', icon: QrCode },
      ]
    },
    {
      name: 'Sales & POS',
      icon: ShoppingCart,
      color: 'from-yellow-500 to-orange-500',
      items: [
        { name: 'POS', href: '/pos', icon: ShoppingCart },
        { name: 'Sales & Invoices', href: '/sales-invoices', icon: ClipboardList },
        { name: 'Cash-ups', href: '/cash-ups', icon: FileText },
        { name: 'Vouchers', href: '/vouchers', icon: Gift },
        { name: 'Discount Logs', href: '/discount-logs', icon: Percent },
      ]
    },
    {
      name: 'Business Management',
      icon: TrendingUp,
      color: 'from-blue-500 to-indigo-600',
      items: [
        { name: 'Customers', href: '/customers', icon: Users, permission: 'view_customers' },
        { name: 'Products', href: '/products', icon: Package, permission: 'manage_products' },
        { name: 'Inventory', href: '/inventory', icon: Package, permission: 'manage_inventory' },
        { name: 'Expenses', href: '/expenses', icon: Receipt, permission: 'manage_expenses' },
        { name: 'Subscriptions', href: '/subscriptions', icon: CreditCard, permission: 'manage_subscriptions' },
        { name: 'Wash Catalog', href: '/wash-catalog', icon: ClipboardList, permission: 'manage_wash_catalog' },
        { name: 'Dealers', href: '/dealers', icon: Building2, permission: 'manage_dealers' },
      ]
    },
    {
      name: 'Analytics & Reports',
      icon: BarChart3,
      color: 'from-purple-500 to-pink-600',
      items: [
        { name: 'Reports', href: '/reports', icon: BarChart3 },
        { name: 'Accounts', href: '/accounts', icon: FileText },
      ]
    },
    {
      name: 'System Administration',
      icon: Settings,
      color: 'from-gray-600 to-gray-800',
      items: [
        { name: 'Branch Management', href: '/branch-management', icon: Building2 },
        { name: 'User Management', href: '/user-management', icon: Shield },
        { name: 'Role Management', href: '/role-management', icon: Shield },
        { name: 'Profile', href: '/profile', icon: User },
      ]
    }
  ],
}

// Legacy flat navigation for backward compatibility
const navigationItems = {
  customer: [
    { name: 'Dashboard', href: '/customer', icon: LayoutDashboard },
    { name: 'My QR Code', href: '/customer/qr', icon: QrCode },
    { name: 'Wash History', href: '/customer/history', icon: Car },
    { name: 'Profile', href: '/customer/profile', icon: User },
  ],
  staff: [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard },
    { name: 'Wash Queue', href: '/wash-queue', icon: List },
    { name: 'Scan QR', href: '/scan', icon: QrCode },
    { name: 'POS', href: '/pos', icon: CreditCard },
    { name: 'Cash-ups', href: '/cash-ups', icon: FileText, permission: 'perform_cash_up' },
    { name: 'Customers', href: '/customers', icon: Users },
    { name: 'Profile', href: '/profile', icon: User },
  ],
  manager: [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard },
    { name: 'Wash Queue', href: '/wash-queue', icon: List },
    { name: 'Scan QR', href: '/scan', icon: QrCode },
    { name: 'POS', href: '/pos', icon: ShoppingCart },
    { name: 'Customers', href: '/customers', icon: Users },
    { name: 'Inventory', href: '/inventory', icon: Package },
    { name: 'Expenses', href: '/expenses', icon: Receipt },
    { name: 'Reports', href: '/reports', icon: BarChart3 },
    { name: 'Subscriptions', href: '/subscriptions', icon: CreditCard },
    { name: 'Cash-ups', href: '/cash-ups', icon: FileText },
    { name: 'Dealers', href: '/dealers', icon: Building2 },
    { name: 'Profile', href: '/profile', icon: User },
    { name: 'User Management', href: '/user-management', icon: Shield },
  ],
  owner: [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard },
    { name: 'Wash Queue', href: '/wash-queue', icon: List },
    { name: 'Scan QR', href: '/scan', icon: QrCode },
    { name: 'POS', href: '/pos', icon: ShoppingCart },
    { name: 'Sales & Invoices', href: '/sales-invoices', icon: ClipboardList },
    { name: 'Cash-ups', href: '/cash-ups', icon: FileText },
    { name: 'Customers', href: '/customers', icon: Users },
    { name: 'Accounts', href: '/accounts', icon: FileText },
    { name: 'Discount Logs', href: '/discount-logs', icon: Percent },
    { name: 'Inventory', href: '/inventory', icon: Package },
    { name: 'Expenses', href: '/expenses', icon: Receipt },
    { name: 'Reports', href: '/reports', icon: BarChart3 },
    { name: 'Subscriptions', href: '/subscriptions', icon: CreditCard },
    { name: 'Dealers', href: '/dealers', icon: Building2 },
    { name: 'Branch Management', href: '/branch-management', icon: Building2 },
    { name: 'Role Management', href: '/role-management', icon: Shield },
    { name: 'Profile', href: '/profile', icon: User },
    { name: 'User Management', href: '/user-management', icon: Shield },
  ],
}

function NavigationGroup({ group, isExpanded, onToggle, onNavigate }) {
  const hasActiveItem = group.items.some(item => {
    const path = window.location.pathname
    return path === item.href || path.startsWith(item.href + '/')
  })

  return (
    <li>
      <div className="space-y-1.5">
        <button
          type="button"
          onClick={onToggle}
          className={cn(
            'flex w-full items-center justify-between rounded-lg border border-border bg-card px-3 py-2.5 text-sm font-medium text-foreground transition-colors',
            'hover:bg-muted/70',
            hasActiveItem && 'ring-1 ring-primary/25'
          )}
        >
          <div className="flex items-center gap-2.5">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-muted text-muted-foreground">
              <group.icon className="h-4 w-4" />
            </div>
            <span className="text-sm font-semibold">{group.name}</span>
          </div>
          <ChevronDown
            className={cn(
              'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
              isExpanded && 'rotate-180'
            )}
          />
        </button>

        <div
          className={cn(
            'overflow-hidden transition-all duration-200',
            isExpanded ? 'max-h-[28rem] opacity-100' : 'max-h-0 opacity-0'
          )}
        >
          <ul className="space-y-0.5 border-l border-border pl-3 ml-1.5">
            {group.items.map((item) => (
              <li key={item.name}>
                <NavLink
                  to={item.href}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors border border-transparent',
                      isActive
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <div
                        className={cn(
                          'flex h-6 w-6 items-center justify-center rounded-md',
                          isActive
                            ? 'bg-primary-foreground/15 text-primary-foreground'
                            : 'bg-muted text-muted-foreground'
                        )}
                      >
                        <item.icon className="h-3.5 w-3.5" />
                      </div>
                      <span>{item.name}</span>
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </li>
  )
}

export default function Sidebar({ onNavigate }) {
  const { role, userProfile } = useAuth()
  const [expandedGroups, setExpandedGroups] = useState(new Set())
  
  const groups = useMemo(() => {
    const raw = navigationGroups[role] || []
    return raw
      .map((group) => ({
        ...group,
        items: (group.items || []).filter(
          (item) => !item.permission || roleService.hasPermission(userProfile, item.permission)
        ),
      }))
      .filter((group) => group.items.length > 0)
  }, [role, userProfile])
  
  // Initialize expanded state for groups with active items
  useEffect(() => {
    const newExpandedGroups = new Set()
    groups.forEach((group, index) => {
      const hasActiveItem = group.items.some(item => {
        const path = window.location.pathname
        return path === item.href || path.startsWith(item.href + '/')
      })
      // Expand groups with active items, or expand all for better UX
      if (hasActiveItem || groups.length <= 3) {
        newExpandedGroups.add(index)
      }
    })
    setExpandedGroups(newExpandedGroups)
  }, [role, groups])

  const toggleGroup = (index) => {
    const newExpandedGroups = new Set(expandedGroups)
    if (newExpandedGroups.has(index)) {
      newExpandedGroups.delete(index)
    } else {
      newExpandedGroups.add(index)
    }
    setExpandedGroups(newExpandedGroups)
  }

  return (
    <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-sidebar px-4 pb-5 pt-4 shadow-sm border-r border-sidebar-border">
      <div className="flex h-14 shrink-0 items-center gap-3 px-1">
        <img
          src="/brand/trendsetter-logo.png"
          alt=""
          className="h-9 w-auto object-contain"
        />
        <div className="min-w-0">
          <h1 className="text-sm font-semibold text-sidebar-foreground truncate">
            TrendSetter
          </h1>
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {role} · Console
          </p>
        </div>
      </div>
      
      {/* Navigation */}
      <nav className="flex flex-1 flex-col">
        <ul role="list" className="flex flex-1 flex-col gap-y-2">
          {groups.map((group, index) => (
            <NavigationGroup
              key={group.name}
              group={group}
              isExpanded={expandedGroups.has(index)}
              onToggle={() => toggleGroup(index)}
              onNavigate={onNavigate}
            />
          ))}
        </ul>
      </nav>

      <div className="mt-auto pt-4 border-t border-sidebar-border">
        <p className="text-center text-[10px] text-muted-foreground">
          Business OS
        </p>
      </div>
    </div>
  )
}